import { FC, ReactElement, useMemo, ComponentProps, useCallback, ReactNode } from 'react'
import moment from 'moment'

import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { getCryptoRewardsPaneFieldsStateOrderDetailsOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { CryptoRewardsOrderId } from 'components/features/rewards/rewardsPane/types/orderResult'
import { isOrdersResultsLoadingSelector } from 'components/features/rewards/rewardsPane/store/selectors/orderState'
import { CryptoRewardsOrderDetailsField } from 'components/features/rewards/rewardsPane/components/CryptoRewardsOrderDetailsField/CryptoRewardsOrderDetailsField'
import { CryptoRewardsOrderType } from 'components/features/rewards/rewardsPane/components/CryptoRewardsOrderType/CryptoRewardsOrderType'
import { CryptoRewardsOrderStatus } from 'components/features/rewards/rewardsPane/components/CryptoRewardsOrderStatus/CryptoRewardsOrderStatus'
import { CRYPTO_REWARDS_ORDER_STATUS_FULFILLED } from 'components/features/rewards/rewardsPane/constants/orders'
import { useGetCryptoCurrencySymbolByIdOrNull } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencySymbolByIdOrNull'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useGetCryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencyNameById'
import TooltipIcon from 'components/features/pfm/Tooltip/TooltipIcon'

import styles from 'components/features/rewards/rewardsPane/containers/CryptoRewardsOrderDetails/CryptoRewardsOrderDetails.styl'
import { CryptoRewardsCryptoOrderSuccessCommonFieldsResponse } from 'components/features/rewards/rewardsPane/types/apiResponses'

type CryptoRewardsOrderDetailsProps = {
  onClose (): void;
}

type CryptoRewardsOrderDetailsReadyProps = CryptoRewardsOrderDetailsProps & {
  order: CryptoRewardsCryptoOrderSuccessCommonFieldsResponse
}

type OrderFieldDetails = ComponentProps<typeof CryptoRewardsOrderDetailsField>

const PANE_TITLE = 'Investment Details'

function formatDate (date: string): string {
  return moment(date).format('lll')
}

const CryptoRewardsOrderDetailsReady: FC<CryptoRewardsOrderDetailsReadyProps> = ({
  order,
  onClose,
}): ReactElement => {
  const { ticker, status, type, created, filled, taxes, fees, amount_usd, quantity, execution_price, reward } = order
  const currencyName = useGetCryptoCurrencyNameById(ticker)
  const currencySymbol = useGetCryptoCurrencySymbolByIdOrNull(ticker)
  const isOrdersDetailsLoading: boolean = useStoreSelector(isOrdersResultsLoadingSelector)

  const orderStatusFields: OrderFieldDetails[] = useMemo(() => {

    return [
      {
        label: 'Order Type',
        value: <CryptoRewardsOrderType orderType={ type === 'buy' ? 'cryptoRewardsBuyMode' : 'cryptoRewardsSellMode' } />,
      },
      {
        label: 'Order Status',
        value: <CryptoRewardsOrderStatus orderStatus={ status } />,
      },
    ]
  }, [status, type])

  const orderDateFields: (OrderFieldDetails | null)[] = useMemo(() => {

    return [
      {
        label: 'Submitted',
        value: formatDate(created),
      },

      status !== CRYPTO_REWARDS_ORDER_STATUS_FULFILLED || !filled
        ? null
        : {
          label: 'Filled date',
          value: formatDate(filled),
        },
    ]
  }, [status, created, filled])

  const orderFeesAndTaxes: OrderFieldDetails[] | null = useMemo(() => {
    if (type === 'buy' || status !== CRYPTO_REWARDS_ORDER_STATUS_FULFILLED) {
      return null
    }
    return [
      {
        label: 'Taxes',
        value: formatUSCurrency(taxes, 'penny'),
      },
      {
        label: 'Fees',
        value: formatUSCurrency(fees, 'penny'),
      },

    ]
  }, [fees, status, taxes, type])

  const orderRewards: (OrderFieldDetails | null)[] = useMemo(() => {

    const filledQuantityField = execution_price === null
      ? null
      : {
        label: 'Filled quantity',
        value: `${ quantity } ${ currencySymbol } at ${ formatUSCurrency(execution_price, 'penny') }`,
      }

    if (type === 'buy') {
      return [
        {
          label: 'Cryptocurrency Bought',
          value: currencyName,
        },
        filledQuantityField,
        {
          label: 'Total Notional',
          value: formatUSCurrency(status !== CRYPTO_REWARDS_ORDER_STATUS_FULFILLED ? amount_usd : reward, 'penny'),
        },
      ]
    }

    return [
      {
        label: 'Cryptocurrency Sold',
        value: currencyName,
      },
      filledQuantityField,
      {
        label: (
          <div className={ styles.estRewards }>
            { status === 'pending' || status === 'cancelled' && 'Est Rewards' }
            { status === 'completed' && 'Rewards' }
            <div className={ styles.tooltipWrapper }>
              <TooltipIcon className={ styles.tooltip } icon='question'>
                The amount you receive may be different due to market volatility.
              </TooltipIcon>
            </div>
          </div>
        ),
        value: formatUSCurrency(status === 'completed' ? reward : amount_usd, 'penny'),
      },
    ]
  }, [type, amount_usd, quantity, execution_price, currencyName, currencySymbol, status, reward])

  const renderOrderFieldOrNull = useCallback((fieldProps: OrderFieldDetails | null): ReactNode => (
    fieldProps && <CryptoRewardsOrderDetailsField key={ String(fieldProps.label) } { ...fieldProps } />
  ), [])
  const renderDelimiter = useCallback((): ReactElement => <hr className={ styles.delimiter } />, [])

  return (
    <CryptoRewardsPanePreset
      isLoading={ isOrdersDetailsLoading }
      title={ PANE_TITLE }
      cancelName='Close'
      onCancel={ onClose }
    >
      { orderStatusFields.map(renderOrderFieldOrNull) }
      { renderDelimiter() }
      { orderDateFields.map(renderOrderFieldOrNull) }
      { renderDelimiter() }
      { orderFeesAndTaxes
        ? (
          <>
            { orderFeesAndTaxes.map(renderOrderFieldOrNull) }
            { renderDelimiter() }
          </>)
        : null }
      { orderRewards.map(renderOrderFieldOrNull) }
    </CryptoRewardsPanePreset>
  )
}

export const CryptoRewardsOrderDetails: FC<CryptoRewardsOrderDetailsProps> = ({ onClose }) => {

  const paneStateOrNull = useStoreSelector(getCryptoRewardsPaneFieldsStateOrderDetailsOrNullSelector)
  const orderRequestedIdOrNull: CryptoRewardsOrderId | null = paneStateOrNull?.openOrderId ?? null

  const order = useStoreSelector((state) => {
    if (!orderRequestedIdOrNull) {
      return null
    }

    const item = state.data.bounty.reward.items?.results.byKey[orderRequestedIdOrNull] ?? null
    if (item?.transaction_type !== 'crypto') {
      return null
    }

    return item
  })

  if (!order) {
    return null
  }

  return (
    <CryptoRewardsOrderDetailsReady
      order={ order }
      onClose={ onClose }
    />
  )

}
